/**
 * Datei-Explorer (Gameserver-Dateien über den Agent, sonst SFTP-Fallback).
 * Sichtbar ist nur, was unterhalb des Roots liegt; geschrieben werden darf NUR
 * in den freigegebenen Ordnern (die harte Grenze erzwingen Agent bzw. die
 * ro/rw-Mounts des SFTP-Containers — die UI sperrt die Buttons nur passend).
 * Reine Helfer oben (getestet).
 */

import { useSignal, useSignalEffect } from "@preact/signals";
import { api, hasPermission } from "../store";
import { PageHead, Panel } from "../components";
import { toast } from "../toast";
import type { ApiResponse } from "../api";
import { t } from "../i18n";

/* ------------------------------------------------------------ Typen */

export interface FsCapsView {
  rootLabel: string;
  writeDirs: string[];
  maxUploadBytes: number;
  maxReadBytes: number;
  allowDelete: boolean;
}

export interface FsServerView {
  id: string;
  online: boolean;
  /** Aktiver Weg: Agent (bevorzugt) oder SFTP-Fallback. */
  via: "agent" | "sftp" | null;
  caps: FsCapsView | null;
}

export interface FsEntry {
  name: string;
  type: "dir" | "file" | "link" | "other";
  size: number;
  mtime: number;
  link?: boolean;
}

interface Listing {
  path: string;
  writable: boolean;
  entries: FsEntry[];
  truncated: boolean;
}

interface EditorState {
  path: string;
  content: string;
  original: string;
  encoding: string;
  mtime: number;
  writable: boolean;
}

export interface TransferRow {
  key: string;
  name: string;
  direction: "up" | "down";
  phase: "uploading" | "transferring" | "downloading" | "done" | "failed";
  progress: number;
  error?: string;
}

/* ------------------------------------------------------ reine Helfer */

export function joinPath(dir: string, name: string): string {
  return dir === "" ? name : `${dir}/${name}`;
}

export function parentPath(path: string): string {
  const i = path.lastIndexOf("/");
  return i < 0 ? "" : path.slice(0, i);
}

/** Breadcrumb-Segmente ab Root. */
export function breadcrumbs(rootLabel: string, path: string): { label: string; path: string }[] {
  const out = [{ label: rootLabel, path: "" }];
  let cur = "";
  for (const part of path.split("/").filter(Boolean)) {
    cur = joinPath(cur, part);
    out.push({ label: part, path: cur });
  }
  return out;
}

/** Darf in diesem Ordner angelegt/hochgeladen werden (Vorab-Anzeige)? */
export function isWritableDir(writeDirs: readonly string[], dir: string): boolean {
  return writeDirs.some((wd) => wd === "" || dir === wd || dir.startsWith(`${wd}/`));
}

export function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

export function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return "—";
  return new Date(sec * 1000).toLocaleString();
}

const TEXT_EXT = /\.(txt|con|desc|xml|inc|ini|cfg|tweak|ai|py|json|log|md|csv|tsv|lst|ssc|bat|sh|yml|yaml|html?|css|js)$/i;

/** Lohnt „Öffnen" im Editor? (Textendung und klein genug) */
export function isTextCandidate(e: FsEntry, maxReadBytes: number): boolean {
  return e.type === "file" && TEXT_EXT.test(e.name) && (maxReadBytes <= 0 || e.size <= maxReadBytes);
}

function icon(e: FsEntry): string {
  if (e.type === "dir") return "▸";
  if (e.type === "link") return "↪";
  if (/\.zip$/i.test(e.name)) return "▣";
  return "·";
}

/* ------------------------------------------------------- Transfers */

async function waitTransfer(id: string): Promise<{ status: string; error?: string | null }> {
  for (;;) {
    const r = await api.call<ApiResponse & { transfer?: { status: string; error?: string | null } }>("fs_transfer_status", { id });
    if (!r.ok || !r.transfer) return { status: "failed", error: r.error ?? "?" };
    if (r.transfer.status === "done" || r.transfer.status === "failed") return r.transfer;
    await new Promise((res) => setTimeout(res, 700));
  }
}

/** Upload per XHR (Fortschritt); Antwort = Transfer-ID oder Fehler. */
function xhrUpload(
  qs: URLSearchParams,
  file: Blob,
  onProgress: (p: number) => void,
): Promise<{ ok: boolean; error?: string; transfer?: { id: string } }> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api?${qs.toString()}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("content-type", "application/octet-stream");
    xhr.upload.onprogress = (ev) => { if (ev.lengthComputable) onProgress(ev.loaded / ev.total); };
    xhr.onload = () => {
      try {
        resolve(JSON.parse(xhr.responseText));
      } catch {
        resolve({ ok: false, error: `HTTP ${xhr.status}` });
      }
    };
    xhr.onerror = () => resolve({ ok: false, error: t("common.error") });
    xhr.send(file);
  });
}

/* ------------------------------------------------------------ View */

export function Files() {
  const servers = useSignal<FsServerView[]>([]);
  const canWrite = useSignal(false);
  const configErrors = useSignal<string[]>([]);
  const server = useSignal<string>("");
  const path = useSignal("");
  const listing = useSignal<Listing | null>(null);
  const loading = useSignal(false);
  const error = useSignal<string | null>(null);
  const editor = useSignal<EditorState | null>(null);
  const transfers = useSignal<TransferRow[]>([]);
  const overwrite = useSignal(false);
  const dragOver = useSignal(false);

  const current = () => servers.value.find((s) => s.id === server.value) ?? null;
  const caps = () => current()?.caps ?? null;

  async function loadServers() {
    const r = await api.call<ApiResponse & { servers?: FsServerView[]; canWrite?: boolean; sftpConfigErrors?: string[] }>("fs_servers");
    if (!r.ok) { error.value = String(r.error ?? t("common.error")); return; }
    servers.value = r.servers ?? [];
    canWrite.value = !!r.canWrite;
    configErrors.value = r.sftpConfigErrors ?? [];
    if (!servers.value.some((s) => s.id === server.value)) {
      server.value = servers.value.find((s) => s.online)?.id ?? servers.value[0]?.id ?? "";
    }
  }

  async function loadDir(p: string = path.value) {
    if (!server.value) return;
    loading.value = true;
    const r = await api.call<ApiResponse & Listing>("fs_list", { server: server.value, path: p });
    loading.value = false;
    if (!r.ok) { error.value = String(r.error ?? t("common.error")); return; }
    error.value = null;
    path.value = r.path;
    listing.value = { path: r.path, writable: r.writable, entries: r.entries, truncated: r.truncated };
  }

  useSignalEffect(() => { void loadServers(); });
  useSignalEffect(() => {
    const sv = server.value;
    if (!sv) return;
    path.value = "";
    editor.value = null;
    void loadDir("");
  });

  function go(p: string) {
    if (editor.value && editor.value.content !== editor.value.original && !confirm(t("files.discardConfirm"))) return;
    editor.value = null;
    void loadDir(p);
  }

  const c = caps();
  const dirWritable = !!c && canWrite.value && (listing.value?.writable ?? false) && isWritableDir(c.writeDirs, path.value);
  const writeBlockReason = !canWrite.value ? t("files.noWritePermission") : t("files.uploadDisabled");

  /* ------------------------------------------------ Aktionen */

  async function simple(action: string, body: Record<string, unknown>, okMsg: string) {
    const r = await api.call<ApiResponse>(action, { server: server.value, ...body });
    if (r.ok) { toast(okMsg, "ok"); await loadDir(); } else toast(String(r.error ?? t("common.error")), "err");
    return r.ok;
  }

  async function openFile(e: FsEntry) {
    const p = joinPath(path.value, e.name);
    const r = await api.call<ApiResponse & { content: string; encoding: string; mtime: number; writable: boolean }>("fs_read", { server: server.value, path: p });
    if (!r.ok) { toast(String(r.error ?? t("common.error")), "err"); return; }
    editor.value = { path: p, content: r.content, original: r.content, encoding: r.encoding, mtime: r.mtime, writable: r.writable && canWrite.value };
  }

  async function saveFile() {
    const ed = editor.value;
    if (!ed) return;
    const r = await api.call<ApiResponse & { mtime: number }>("fs_write", { server: server.value, path: ed.path, content: ed.content, encoding: ed.encoding, mtime: ed.mtime });
    if (r.ok) {
      editor.value = { ...ed, original: ed.content, mtime: r.mtime };
      toast(t("files.saved"), "ok");
      await loadDir();
    } else toast(String(r.error ?? t("common.error")), "err");
  }

  function setRow(key: string, patch: Partial<TransferRow>) {
    transfers.value = transfers.value.map((x) => (x.key === key ? { ...x, ...patch } : x));
  }

  async function uploadFiles(files: File[]) {
    if (!dirWritable || files.length === 0) return;
    const dir = path.value;
    const rows: TransferRow[] = files.map((f, i) => ({
      key: `${Date.now()}-${i}-${f.name}`,
      name: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name,
      direction: "up",
      phase: "uploading",
      progress: 0,
    }));
    transfers.value = [...rows, ...transfers.value];
    // Nacheinander: schont Bandbreite und Agent, Reihenfolge bleibt nachvollziehbar.
    for (let i = 0; i < files.length; i++) {
      const row = rows[i]!;
      const qs = new URLSearchParams({ action: "fs_upload", server: server.value, dir, name: row.name, overwrite: overwrite.value ? "1" : "0" });
      const r = await xhrUpload(qs, files[i]!, (p) => setRow(row.key, { progress: p }));
      if (!r.ok || !r.transfer) { setRow(row.key, { phase: "failed", error: r.error }); continue; }
      setRow(row.key, { phase: "transferring", progress: 1 });
      const done = await waitTransfer(r.transfer.id);
      setRow(row.key, done.status === "done" ? { phase: "done" } : { phase: "failed", error: done.error ?? undefined });
    }
    await loadDir();
  }

  async function download(e: FsEntry) {
    const p = joinPath(path.value, e.name);
    const key = `${Date.now()}-dl-${e.name}`;
    transfers.value = [{ key, name: e.name, direction: "down", phase: "downloading", progress: 0 }, ...transfers.value];
    const r = await api.call<ApiResponse & { transfer?: { id: string } }>("fs_download_prepare", { server: server.value, path: p });
    if (!r.ok || !r.transfer) { setRow(key, { phase: "failed", error: String(r.error ?? "") }); return; }
    const done = await waitTransfer(r.transfer.id);
    if (done.status !== "done") { setRow(key, { phase: "failed", error: done.error ?? undefined }); return; }
    setRow(key, { phase: "done", progress: 1 });
    const a = document.createElement("a");
    a.href = `/api?action=fs_download_get&id=${encodeURIComponent(r.transfer.id)}`;
    a.download = e.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function newFolder() {
    const name = prompt(t("files.newFolderPrompt"));
    if (name) await simple("fs_mkdir", { path: path.value, name }, t("files.created"));
  }

  async function rename(e: FsEntry) {
    const name = prompt(t("files.renamePrompt"), e.name);
    if (name && name !== e.name) await simple("fs_rename", { path: joinPath(path.value, e.name), name }, t("files.renamed"));
  }

  async function del(e: FsEntry) {
    const msg = `${t("files.deleteConfirmPre")}${e.name}${e.type === "dir" ? t("files.deleteConfirmDirSuffix") : ""}?`;
    if (confirm(msg)) await simple("fs_delete", { path: joinPath(path.value, e.name) }, t("files.deleted"));
  }

  async function extract(e: FsEntry) {
    if (!confirm(t("files.extractConfirm"))) return;
    await simple("fs_extract", { path: joinPath(path.value, e.name), overwrite: overwrite.value }, t("files.extracted"));
  }

  function pickFiles(folder: boolean) {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    if (folder) input.setAttribute("webkitdirectory", "");
    input.onchange = () => void uploadFiles(Array.from(input.files ?? []));
    input.click();
  }

  /* ------------------------------------------------ Render */

  const cur = current();
  const ed = editor.value;
  const entries = listing.value?.entries ?? [];
  const phaseLabel: Record<TransferRow["phase"], string> = {
    uploading: t("files.statusUploading"),
    transferring: t("files.statusTransferring"),
    downloading: t("files.statusDownloading"),
    done: t("files.statusDone"),
    failed: t("files.statusFailed"),
  };

  return (
    <div class="view-enter">
      <PageHead title={t("files.title")} sub={t("files.sub")}>
        {servers.value.length > 0 && (
          <select class="select" value={server.value} onChange={(e) => (server.value = (e.target as HTMLSelectElement).value)}>
            {servers.value.map((s) => (
              <option key={s.id} value={s.id}>{s.id}{s.online ? "" : ` (${t("files.agentOffline")})`}</option>
            ))}
          </select>
        )}
        {cur?.via && (
          <span class={`badge ${cur.via === "agent" ? "accent" : ""}`} style="white-space:nowrap" title={cur.via === "sftp" ? t("files.viaSftpHint") : ""}>
            {cur.via === "agent" ? t("files.viaAgent") : t("files.viaSftp")}
          </span>
        )}
        <button class="btn sm ghost" onClick={() => { void loadServers(); void loadDir(); }}>{t("common.refresh")}</button>
      </PageHead>
      {configErrors.value.length > 0 && canWrite.value && (
        <div class="form-err" style="margin-bottom:var(--s3)">{t("files.sftpConfigErrors")}: {configErrors.value.join("; ")}</div>
      )}

      {servers.value.length === 0 ? (
        <Panel><div class="empty">{error.value ?? t("files.noServers")}</div></Panel>
      ) : !cur?.online || !c ? (
        <Panel><div class="empty">{t("files.agentOffline")}</div></Panel>
      ) : (
        <div class="stack">
          <Panel
            title={
              <span class="fx-crumbs">
                {breadcrumbs(c.rootLabel, path.value).map((b, i, all) => (
                  <span key={b.path}>
                    {i < all.length - 1
                      ? <button class="fx-crumb" onClick={() => go(b.path)}>{b.label}</button>
                      : <span class="fx-crumb current">{b.label}</span>}
                    {i < all.length - 1 && <span class="faint"> / </span>}
                  </span>
                ))}
              </span>
            }
            actions={
              <>
                <span class={`badge ${dirWritable ? "accent" : ""}`} title={dirWritable ? "" : writeBlockReason}>
                  {dirWritable ? t("files.writable") : t("files.readOnly")}
                </span>
                <label class="row" style="gap:6px" title={t("files.overwrite")}>
                  <input type="checkbox" checked={overwrite.value} disabled={!dirWritable} onChange={(e) => (overwrite.value = (e.target as HTMLInputElement).checked)} />
                  <span class="muted">{t("files.overwrite")}</span>
                </label>
                <button class="btn sm" disabled={!dirWritable} title={dirWritable ? "" : writeBlockReason} onClick={() => void newFolder()}>{t("files.newFolder")}</button>
                <button class="btn sm" disabled={!dirWritable} title={dirWritable ? "" : writeBlockReason} onClick={() => pickFiles(false)}>{t("files.uploadFiles")}</button>
                <button class="btn sm" disabled={!dirWritable} title={dirWritable ? "" : writeBlockReason} onClick={() => pickFiles(true)}>{t("files.uploadFolder")}</button>
              </>
            }
          >
            <div
              class={`fx-drop ${dragOver.value ? "active" : ""}`}
              onDragOver={(e) => { if (dirWritable) { e.preventDefault(); dragOver.value = true; } }}
              onDragLeave={() => (dragOver.value = false)}
              onDrop={(e) => {
                e.preventDefault();
                dragOver.value = false;
                if (dirWritable) void uploadFiles(Array.from(e.dataTransfer?.files ?? []));
              }}
            >
              {error.value && <div class="form-err">{error.value}</div>}
              <table class="score fx-table">
                <thead>
                  <tr><th>{t("files.name")}</th><th class="num">{t("common.size")}</th><th>{t("files.modified")}</th><th></th></tr>
                </thead>
                <tbody>
                  {path.value !== "" && (
                    <tr class="fx-row" onClick={() => go(parentPath(path.value))}>
                      <td colSpan={4}><span class="fx-icon">▴</span> <span class="muted">.. ({t("files.up")})</span></td>
                    </tr>
                  )}
                  {entries.map((e) => {
                    const mutable = dirWritable;
                    return (
                      <tr key={e.name} class="fx-row">
                        <td data-label={t("files.name")}>
                          <span class="fx-icon">{icon(e)}</span>{" "}
                          {e.type === "dir"
                            ? <button class="fx-link" onClick={() => go(joinPath(path.value, e.name))}>{e.name}</button>
                            : <span class={e.type === "link" ? "muted" : ""}>{e.name}</span>}
                          {e.link && e.type !== "link" && <span class="faint"> ↪</span>}
                          {e.type === "dir" && c.writeDirs.includes(joinPath(path.value, e.name)) && (
                            <span class="badge accent" style="margin-left:6px;font-size:10px">{t("files.writable")}</span>
                          )}
                        </td>
                        <td class="num muted" data-label={t("common.size")}>{e.type === "file" ? formatSize(e.size) : ""}</td>
                        <td class="muted" data-label={t("files.modified")}>{formatTime(e.mtime)}</td>
                        <td class="num fx-actions">
                          {isTextCandidate(e, c.maxReadBytes) && <button class="btn sm ghost" onClick={() => void openFile(e)}>{t("files.open")}</button>}
                          {e.type === "file" && <button class="btn sm ghost" onClick={() => void download(e)}>{t("files.download")}</button>}
                          {mutable && e.type === "file" && /\.zip$/i.test(e.name) && <button class="btn sm ghost" onClick={() => void extract(e)}>{t("files.extract")}</button>}
                          {mutable && <button class="btn sm ghost" onClick={() => void rename(e)}>{t("files.rename")}</button>}
                          {mutable && c.allowDelete && <button class="btn sm ghost" onClick={() => void del(e)}>{t("common.delete")}</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!loading.value && entries.length === 0 && <div class="empty">{t("files.empty")}</div>}
              {listing.value?.truncated && <div class="faint" style="padding:var(--s2)">{t("files.truncated")}</div>}
              {dirWritable && <div class="faint fx-drop-hint">{t("files.dropHint")}</div>}
            </div>
          </Panel>

          {ed && (
            <Panel
              title={<span class="mono">{ed.path}</span>}
              actions={
                <>
                  <span class="badge">{ed.encoding}</span>
                  {!ed.writable && <span class="badge">{t("files.readOnlyFile")}</span>}
                  {ed.content !== ed.original && <span class="badge accent">{t("files.unsaved")}</span>}
                  {ed.writable && <button class="btn sm primary" disabled={ed.content === ed.original} onClick={() => void saveFile()}>{t("common.save")}</button>}
                  <button class="btn sm ghost" onClick={() => {
                    if (ed.content !== ed.original && !confirm(t("files.discardConfirm"))) return;
                    editor.value = null;
                  }}>{t("common.close")}</button>
                </>
              }
            >
              <textarea
                class="textarea mono fx-editor"
                spellcheck={false}
                readOnly={!ed.writable}
                value={ed.content}
                onInput={(e) => (editor.value = { ...ed, content: (e.target as HTMLTextAreaElement).value })}
              />
            </Panel>
          )}

          {transfers.value.length > 0 && (
            <Panel
              title={t("files.transfers")}
              actions={<button class="btn sm ghost" onClick={() => (transfers.value = transfers.value.filter((x) => x.phase !== "done"))}>{t("files.clearDone")}</button>}
            >
              <div class="stack" style="gap:6px">
                {transfers.value.map((x) => (
                  <div key={x.key} class="fx-transfer">
                    <span class="mono fx-transfer-name">{x.direction === "up" ? "↑" : "↓"} {x.name}</span>
                    <span class="fx-progress"><span style={`width:${Math.round(x.progress * 100)}%`} class={x.phase === "failed" ? "failed" : ""} /></span>
                    <span class={x.phase === "failed" ? "form-err" : "muted"}>{phaseLabel[x.phase]}{x.error ? `: ${x.error}` : ""}</span>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          <Panel title={t("files.writeDirs")}>
            {c.writeDirs.length === 0 ? <div class="muted">{t("files.noWriteDirs")}</div> : (
              <div class="row" style="gap:6px;flex-wrap:wrap">
                {c.writeDirs.map((d) => (
                  <button key={d} class="btn sm ghost mono" onClick={() => go(d)}>{c.rootLabel}/{d}</button>
                ))}
              </div>
            )}
            {!hasPermission("action.files_write") && <div class="faint" style="margin-top:6px">{t("files.noWritePermission")}</div>}
          </Panel>
        </div>
      )}
    </div>
  );
}
